import React, { useMemo, useState } from 'react';
import { ShoppingBag, SlidersHorizontal } from 'lucide-react';
import { AppButton } from '../ui/AppButton';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

const initialFilters = {
  dataType: '',
  typology: '',
  country: '',
  minPrice: '',
  maxPrice: '',
};

/**
 * Offer discovery and acceptance panel for data/service marketplace.
 */
export const DataMarketplace = ({
  offers,
  loading,
  onRefresh,
  onAcceptOffer,
  onDeclineOffer,
  acceptingOfferId,
  walletAddress,
}) => {
  const isOwnOffer = (offer) =>
    walletAddress && offer.provider &&
    walletAddress.toLowerCase() === offer.provider.toLowerCase();
  const [filters, setFilters] = useState(initialFilters);

  const filteredOffers = useMemo(() => {
    return (offers || []).filter((offer) => {
      const matchesType = !filters.dataType || offer.dataType === filters.dataType;
      const typology = offer?.metadata?.typology || '';
      const country = offer?.metadata?.country || '';
      const priceEth = Number(offer.priceEth || 0);

      const matchesTypology =
        !filters.typology ||
        typology.toLowerCase().includes(filters.typology.toLowerCase());
      const matchesCountry =
        !filters.country ||
        country.toLowerCase().includes(filters.country.toLowerCase());
      const matchesMin = !filters.minPrice || priceEth >= Number(filters.minPrice);
      const matchesMax = !filters.maxPrice || priceEth <= Number(filters.maxPrice);

      return matchesType && matchesTypology && matchesCountry && matchesMin && matchesMax;
    });
  }, [offers, filters]);

  return (
    <section className="panel-card" aria-labelledby="marketplace-title">
      <div className="panel-header">
        <h2 id="marketplace-title">Data Offer Marketplace</h2>
        <button type="button" onClick={onRefresh} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
          Refresh
        </button>
      </div>

      <div className="filters-grid marketplace-filters">
        <input
          placeholder="Data type"
          value={filters.dataType}
          onChange={(event) =>
            setFilters((current) => ({ ...current, dataType: event.target.value }))
          }
          aria-label="Filter by data type"
          className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
        />
        <input
          placeholder="Building typology"
          value={filters.typology}
          onChange={(event) =>
            setFilters((current) => ({ ...current, typology: event.target.value }))
          }
          aria-label="Filter by building typology"
          className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
        />
        <input
          placeholder="Country"
          value={filters.country}
          onChange={(event) =>
            setFilters((current) => ({ ...current, country: event.target.value }))
          }
          aria-label="Filter by country"
          className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
        />
        <input
          placeholder="Min price ETH"
          type="number"
          min="0"
          value={filters.minPrice}
          onChange={(event) =>
            setFilters((current) => ({ ...current, minPrice: event.target.value }))
          }
          aria-label="Minimum price filter"
          className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
        />
        <input
          placeholder="Max price ETH"
          type="number"
          min="0"
          value={filters.maxPrice}
          onChange={(event) =>
            setFilters((current) => ({ ...current, maxPrice: event.target.value }))
          }
          aria-label="Maximum price filter"
          className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
        />
      </div>

      <div style={{ marginTop: '0.75rem' }}>
      {loading ? (
        <SkeletonLoader rows={4} />
      ) : (
        <div className="offer-grid">
          {filteredOffers.length === 0 && (
            <EmptyState
              icon={offers.length === 0 ? ShoppingBag : SlidersHorizontal}
              title={offers.length === 0 ? 'No offers available' : 'No matches'}
              message={
                offers.length === 0
                  ? 'Publish a data offer above to list it in the marketplace.'
                  : 'No offers match the current filters. Try adjusting your search.'
              }
            />
          )}
          {filteredOffers.map((offer) => (
            <article key={`${offer.onChainOfferId ?? offer.id}-${offer.dataHash}`} className="offer-card marketplace-offer-card">
              <h3>{offer.dataType}</h3>
              <p>
                <strong>Provider:</strong> {offer.providerDisplay || offer.provider}
              </p>
              <p>
                <strong>Price:</strong>{' '}
                {Number(offer.priceEth).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 6,
                })}{' '}
                ETH
              </p>
              <p>
                <strong>Data hash:</strong> {offer.dataHash}
              </p>
              <p>
                <strong>Expiry:</strong>{' '}
                {offer.validUntil ? new Date(offer.validUntil * 1000).toLocaleString() : 'N/A'}
              </p>
              <div className="offer-card-actions">
                <AppButton
                  type="button"
                  variant={isOwnOffer(offer) ? 'ghost' : 'primary'}
                  onClick={() => !isOwnOffer(offer) && onAcceptOffer(offer)}
                  disabled={
                    isOwnOffer(offer) ||
                    acceptingOfferId === (offer.onChainOfferId ?? offer.id)
                  }
                  aria-label={isOwnOffer(offer) ? 'You created this offer' : 'Accept offer'}
                >
                  {isOwnOffer(offer)
                    ? 'Your Offer'
                    : acceptingOfferId === (offer.onChainOfferId ?? offer.id)
                    ? 'Pending...'
                    : 'Accept Offer'}
                </AppButton>
                <AppButton
                  type="button"
                  variant="ghost"
                  onClick={() => onDeclineOffer?.(offer)}
                  disabled={acceptingOfferId === (offer.onChainOfferId ?? offer.id)}
                >
                  Decline
                </AppButton>
              </div>
            </article>
          ))}
        </div>
      )}
      </div>
    </section>
  );
};
